import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Input from '@/components/basicComponents/input';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { TwoColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { ApolloError, useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { UPDATE_LEGAL_ENTITY_MUTATION } from '../queries';
import {
  EditInvestorFormDevider,
  EditInvestorFormFieldsWrapper,
  EditInvestorFormFooter,
  EditInvestorModalPageForm,
  EditInvestorModalPageWrapper
} from '../styles';
import { IEditInvestorFormData } from '../types';

interface DataIntegrationModalPageProps {
  dataIntegrationFormData: IEditInvestorFormData['dataIntegration'];
  refetchEntityOrGroupData: () => Promise<void>;
  handleCloseModalPage: () => void;
}

export const DataIntegrationModalPage = ({
  dataIntegrationFormData,
  refetchEntityOrGroupData,
  handleCloseModalPage
}: DataIntegrationModalPageProps) => {
  const params = useParams();
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const {
    control,
    formState: { isSubmitting },
    reset,
    handleSubmit
  } = useForm<IEditInvestorFormData['dataIntegration']>();

  const entityId = params?.entityId || '';

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [updateLegalEntity] = useMutation(UPDATE_LEGAL_ENTITY_MUTATION);

  useEffect(() => {
    if (!dataIntegrationFormData) return;
    reset(dataIntegrationFormData);
  }, [dataIntegrationFormData]);

  const onSubmit = async (data: IEditInvestorFormData['dataIntegration']) => {
    try {
      setErrorMessage(null);

      const { advisorCRMId, advisorReportingAccountNumber } = data;

      await updateLegalEntity({
        variables: {
          data: {
            id: entityId,
            advisorCRMId,
            advisorReportingAccountNumber
          }
        }
      });

      await refetchEntityOrGroupData();
      handleCloseModalPage();
    } catch (err: unknown) {
      const error = err as ApolloError;
      setErrorMessage(error?.message || defaultErrorMessage);
    }
  };

  return (
    <>
      {isSubmitting && (
        <ModalWrappedContent isOpen={isSubmitting} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleCloseModalPage} backToTitle="Edit Investor" />} />
        <PageTitle title="Data Integration" />
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <EditInvestorModalPageWrapper>
          <EditInvestorModalPageForm onSubmit={handleSubmit(onSubmit)}>
            <EditInvestorFormFieldsWrapper>
              <TwoColumnWrapper isMobile={isMobile}>
                <Controller
                  name="advisorCRMId"
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      value={value || ''}
                      onChange={onChange}
                      errorText={error?.message}
                      label="Advisor CRM ID"
                      styleType="form"
                      size="md"
                      tooltipWidth={400}
                      tooltip="This identifier is used when bulk updating entities using the investor entity upload file or through an integration with an external CRM."
                    />
                  )}
                />
                <Controller
                  name="advisorReportingAccountNumber"
                  control={control}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      value={value || ''}
                      onChange={onChange}
                      errorText={error?.message}
                      label="Advisor Reporting Account Number"
                      styleType="form"
                      size="md"
                      tooltipWidth={400}
                      tooltip="This identifier is used when you export reporting data for upload into your reporting system or another external system."
                    />
                  )}
                />
              </TwoColumnWrapper>
              <TwoColumnWrapper isMobile={isMobile}>
                <Controller
                  name="administratorCode"
                  control={control}
                  render={({ field: { value } }) => (
                    <Input
                      value={value || ''}
                      label="Administrator Code"
                      styleType="disabled"
                      size="md"
                      tooltipWidth={400}
                      tooltip="This is a static identifier assigned by the fund administrator"
                    />
                  )}
                />
              </TwoColumnWrapper>
              <EditInvestorFormDevider />
            </EditInvestorFormFieldsWrapper>
            <EditInvestorFormFooter>
              <Button styleType="outline" onClick={handleCloseModalPage} type="button" size="lg">
                Cancel
              </Button>
              <Button styleType={isSubmitting ? 'disabled' : 'default'} type="submit" size="lg">
                Save
              </Button>
            </EditInvestorFormFooter>
          </EditInvestorModalPageForm>
        </EditInvestorModalPageWrapper>
      </MainWrap>
    </>
  );
};
